import React, { useEffect, useState } from "react";
import "./dashboard.css";

import chatImg from "../../../assets/tenant/chat.png";

import TenantHeader from "../../common/header/TenantHeader";
import ProfileCard from "../../common/profileCard/ProfileCard";
import LeaseDetails from "../../common/leaseDetails/LeaseDetails";
import UnitCardItem from "../../../newComponents/unit/UnitCardItem";
import DynamicTabs from "../../../newComponents/tabs/DynamicTabs";
import SimpleSpinner from "../../../components/loader";

import { getTenantDashboardTabs } from "../tabs/tenantDashboardTabs";
import { useTenantData } from "../../hooks/useTenantData";
import { useUnitData } from "../../hooks/useUnitData";
import { usePayments } from "../../hooks/usePayments";
import LeaseAgreement from "../../../components/generatePdfDocument/leaseAgreement";
import LeaseApplication from "../../../components/generatePdfDocument/leaseApplication";
import { Navigate, Route, Routes, useNavigate } from "react-router";
import apiHelper from "../../../utils/apiHelper";
import {
  LeaseAgreementStatusTypesEnum,
  TenantService,
} from "@propertelligent/client-api";
import StatusHeader from "../../../components/TenantOnboard/component/StatusHeader/StatusHeader";
import RenewalLease from "../../../components/tenants/RenewalLease";
import TenantGoodbyePage from "../TenantGoodbye/TenantGoodbyePage";
import { UserLoginData } from "../../../components/common/charts/Enums";
import { PubSub, TopicsEnum } from "../../../utils/pubsub";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("payments");
  const [selectedFilter, setSelectedFilter] = useState("All Payments");
  const [initialLoading, setInitialLoading] = useState(true);

  // ----- Tenant Info -----
  const tenantInfo = JSON.parse(sessionStorage.getItem("tenantInfo") || "{}");
  // const { tenantId, unitId } = tenantInfo;
  const tenantId = Number(
    tenantInfo.tenantId ?? sessionStorage.getItem(UserLoginData.tenant_id),
  );
  const unitId = tenantInfo.unitId;
  // ----- Hooks -----
  const { tenantData, loading: tenantLoading, refetchTenantData } =
    useTenantData(tenantId);
  const { unitData, loading: unitLoading } = useUnitData(unitId);

  // ----- Chat -----
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [OrgDetail, setOrgDetail] = useState<any>();

  const { filteredRows, loading, fetchData, sentryRef } = usePayments(
    activeTab === "payments",
    selectedFilter,
    tenantId,
  );

  // ----- Initial page loading -----
  useEffect(() => {
    if (!tenantLoading && !unitLoading) setInitialLoading(false);
  }, [tenantLoading, unitLoading]);

  // ----- Fetch data when tab changes -----
  useEffect(() => {
    if (activeTab === "payments") {
      fetchData(1);
    }
  }, [activeTab]);

  // ----- Dropdown menu -----
  const menuItems = [
    { label: "All Payments", action: () => setSelectedFilter("All Payments") },
    { label: "Next 30 Days", action: () => setSelectedFilter("Next 30 Days") },
    {
      label: "Next 3 Months",
      action: () => setSelectedFilter("Next 3 Months"),
    },
  ];

  // ----- Tabs config -----
  const tabs = getTenantDashboardTabs(
    filteredRows,
    loading,
    fetchData,
    sentryRef,
    setActiveTab,
    selectedFilter,
    tenantId,
    unitId,
    activeTab,
    menuItems,
  );

  const onViewAgreement = () => {
    navigate("/lease-agreement");
  };
  const onViewApplication = () => {
    navigate("/lease-application");
  };

  const handleChat = () => {
    setIsChatOpen(!isChatOpen);
  };

  useEffect(() => {
    if (!tenantId || isNaN(tenantId)) return;

    apiHelper(
      TenantService.getApiTenantOrganizationHeaderInfo,
      { showNotification: false },
      tenantId,
    )
      .then((res) => {
        setOrgDetail(res?.data?.success);
      })
      .catch((error) => {
        console.log("Error in getApiTenant1:", error);
      });
  }, [tenantId]);

  useEffect(() => {
    const unsubscribe = PubSub.subscribe(
      TopicsEnum.RefreshLeaseData.toString(),
      () => {
        refetchTenantData();
      },
    );
    return () => unsubscribe();
  }, [refetchTenantData]);

  const handleActionClick = () => {
    if (
      tenantData?.leaseAgreementStatus ===
      LeaseAgreementStatusTypesEnum.RenewalInProgess
    ) {
      navigate("/tenant-signature");
    }
  };

  // ----- Tenant Goodbye -----
  if (!tenantData?.unitId || tenantData?.unitId === 0) {
    if (!initialLoading) {
      return (
        <TenantGoodbyePage
          tenantName={tenantInfo.firstName}
          onLogout={() => {
            sessionStorage.clear();
            localStorage.clear();
            navigate("/login", { replace: true });
          }}
        />
      );
    }
  }

  return (
    <div className="dashboard-container">
      {initialLoading ? (
        <div className="dashboard-loader-wrapper">
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : (
        <>
          <TenantHeader
            chatIconSrc={chatImg}
            onChatClick={handleChat}
            tenantId={tenantId}
            isChatOpen={isChatOpen}
            OrgDetail={OrgDetail}
          />
          {tenantData?.leaseAgreementStatus ===
            LeaseAgreementStatusTypesEnum.RenewalInProgess && (
            <div className="px-3">
              <StatusHeader
                role="tenant"
                status={"renew"}
                onActionClick={handleActionClick}
                disableApprove={undefined}
              />
            </div>
          )}
          <div className="tenant-dashboard-content">
            {/* -------- Profile & Lease Section -------- */}
            <div className="pofile-lease-section">
              <ProfileCard
                name={tenantData?.fullName}
                email={tenantData?.email}
                phone={tenantData?.formatted?.PhoneNumber}
                initials={tenantData?.initials}
                leaseRemainingMonths={tenantData?.leaseRemainingMonths}
                tenancySince={tenantData?.tenancySince}
                onLogout={() => {
                  sessionStorage.clear();
                  window.location.reload();
                  navigate("/login");
                }}
              />

              <LeaseDetails
                leaseType={tenantData?.formatted?.LeaseTypeId}
                startDate={tenantData?.formatted?.StartDateDateOnly}
                endDate={tenantData?.formatted?.EndDateDateOnly}
                rent={tenantData?.formatted?.Rent}
                securityDeposit={tenantData?.formatted?.SecurityDeposit}
                rentRaw={tenantData?.formatted?.RentRaw}
                securityDepositRaw={tenantData?.formatted?.SecurityDepositRaw}
                onViewAgreement={onViewAgreement}
                onViewApplication={onViewApplication}
                tenantData={tenantData}
              />
            </div>

            {/* -------- Main Dashboard Section -------- */}
            <div className="main-dashboard-section">
              <UnitCardItem
                listingData={unitData}
                IsFooter={false}
                tenantInfo={tenantData}
                isNotLink={true}
              />

              <Routes>
                <Route
                  index
                  element={
                    <div className="dashboard-with-tabs">
                      <DynamicTabs tabs={tabs} defaultActiveTab="payments" />
                    </div>
                  }
                />
                <Route
                  path="lease-application"
                  element={<LeaseApplication tenantData={tenantData} />}
                />
                <Route
                  path="lease-agreement"
                  element={<LeaseAgreement tenantData={tenantData} />}
                />
                <Route
                  path="tenant-signature"
                  element={
                    <RenewalLease role="tenant" tenantData={tenantData} />
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
